import { useCallback, useEffect, useState } from 'react';
import { WorkPermitRequirement, WorkTaskDTO2 } from '../api/api';
import NotificationService from '../services/NotificationService';
import TaskService from '../services/TaskService';
import TaskTypeService from '../services/TaskTypeService';
import { getWorkPermitTaskTypeId } from '../utils/constants';
import { log } from '../utils/logging/log';
import { getRelatives, getWorkPermitTaskIds, isWorkPermitRelevant } from '../utils/workPermitTaskSeed';

const getTaskTypeIdsRequiringWorkPermit = async (): Promise<number[]> => {
  const taskTypes = await TaskTypeService.getActiveTaskTypes();

  return (taskTypes.taskTypeDTOs ?? [])
    .filter((taskType) => taskType.workPermitRequirement === WorkPermitRequirement.Required && taskType.id)
    .map((taskType) => taskType.id as number);
};

/**
 * The work permits (Arbejdstilladelser (KOS)) in the immediate hierarchy of a task, and whether they
 * matter for the task at all. The task types of the relatives are only fetched when the task and its
 * relatives do not settle that on their own.
 */
const useWorkPermits = (task?: WorkTaskDTO2) => {
  const [showWorkPermits, setShowWorkPermits] = useState(false);
  const [workPermitTaskIds, setWorkPermitTaskIds] = useState<number[]>([]);
  const [workPermits, setWorkPermits] = useState<WorkTaskDTO2[]>([]);
  const [isLoadingWorkPermits, setIsLoadingWorkPermits] = useState(false);

  useEffect(() => {
    const workPermitTaskTypeId = getWorkPermitTaskTypeId();
    if (!task?.id || isNaN(workPermitTaskTypeId)) {
      setShowWorkPermits(false);
      setWorkPermitTaskIds([]);
      return;
    }

    let isCurrent = true;
    const resolveWorkPermits = async () => {
      const parentTask = task.parent?.workTaskId ? await TaskService.getTask(task.parent.workTaskId) : undefined;

      let isRelevant = isWorkPermitRelevant(task, parentTask, workPermitTaskTypeId, []);
      if (!isRelevant && getRelatives(task, parentTask).length > 0) {
        const taskTypeIdsRequiringWorkPermit = await getTaskTypeIdsRequiringWorkPermit();
        isRelevant = isWorkPermitRelevant(task, parentTask, workPermitTaskTypeId, taskTypeIdsRequiringWorkPermit);
      }

      if (!isCurrent) return;
      const ids = isRelevant ? getWorkPermitTaskIds(task, parentTask, workPermitTaskTypeId) : [];
      setShowWorkPermits(isRelevant);
      setWorkPermitTaskIds((previousIds) => (previousIds.join() === ids.join() ? previousIds : ids));
    };

    resolveWorkPermits().catch((error) => log(error));

    return () => {
      isCurrent = false;
    };
  }, [task]);

  const fetchWorkPermits = useCallback(() => {
    if (workPermitTaskIds.length === 0) {
      setWorkPermits([]);
      return;
    }

    setIsLoadingWorkPermits(true);
    Promise.all(workPermitTaskIds.map((workPermitTaskId) => TaskService.getTask(workPermitTaskId)))
      .then((_workPermits) => setWorkPermits(_workPermits))
      .catch((error) => {
        log(error);
        NotificationService.error('Kunne ikke hente arbejdstilladelserne');
      })
      .finally(() => setIsLoadingWorkPermits(false));
  }, [workPermitTaskIds]);

  useEffect(() => {
    fetchWorkPermits();
  }, [fetchWorkPermits]);

  return { showWorkPermits, workPermits, isLoadingWorkPermits, fetchWorkPermits };
};

export default useWorkPermits;
